import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/adminService';
import api from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faCog, faUserTie, faEdit, faTrash, faPlus, faMinus, faFileUpload, faDownload, faCheckCircle, faTimesCircle, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { User, Role, Rank, Task, OperationType, Feedback } from '../types';
import SettingsPanel from '../components/SettingsPanel';

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'user' | 'location' | 'channel' | 'supervisor' | 'role'>('user');
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    type: '',
    id: 0,
    name: ''
  });

  // Real data from API
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [ranks, setRanks] = useState<Rank[]>([]);
  const [loading, setLoading] = useState({ users: false, roles: false, ranks: false, locations: false, radioChannels: false, tasks: false, operationTypes: false });
  const [error, setError] = useState({ users: '', roles: '', ranks: '', locations: '', radioChannels: '', tasks: '', operationTypes: '' });
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Form data state for user editing
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    telNumber: '',
    rankID: '',
    password: '',
    typeID: '1'
  });

  // Form data state for role editing
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    nameShort: 'xxx', // Always set to 'xxx' for new roles
    description: ''
  });

  // Settings state
  const [maxTrupps, setMaxTrupps] = useState(4);
  const [newFunkkanal, setNewFunkkanal] = useState('');
  const [newStandort, setNewStandort] = useState('');
  const [standardFunkkanaele, setStandardFunkkanaele] = useState<string[]>([]);
  const [standardStandorte, setStandardStandorte] = useState<string[]>([]);
  const [radioChannelsWithIds, setRadioChannelsWithIds] = useState<any[]>([]);
  const [locationsWithIds, setLocationsWithIds] = useState<any[]>([]);
  const [quickTruppCreation, setQuickTruppCreation] = useState(false);
  const [deferredAtemschutz, setDeferredAtemschutz] = useState(false);
  const [truppNamingMode, setTruppNamingMode] = useState<'leader' | 'number'>('leader');
  const [truppNamingModeLoading, setTruppNamingModeLoading] = useState(false);
  const [orgShortName, setOrgShortName] = useState('');
  const [orgShortNameDraft, setOrgShortNameDraft] = useState('');
  const [orgShortNameLoading, setOrgShortNameLoading] = useState(false);
  const [orgShortNameSaving, setOrgShortNameSaving] = useState(false);
  const [orgShortNameError, setOrgShortNameError] = useState('');
  const [truppNamePrefixEnabled, setTruppNamePrefixEnabled] = useState(false);
  const [truppNamePrefixLoading, setTruppNamePrefixLoading] = useState(false);
  const DEFAULT_STATUS_UPDATE_SCREENS: { id: string; label: string; enabled: boolean; order: number }[] = [
    { id: 'status', label: 'Status', enabled: true, order: 1 },
    { id: 'standort', label: 'Standort', enabled: true, order: 2 },
    { id: 'aufgabe', label: 'Aufgabe', enabled: true, order: 3 },
    { id: 'druck', label: 'Druck', enabled: true, order: 4 },
    { id: 'funkkanal', label: 'Funkkanal', enabled: true, order: 5 },
    { id: 'feedback', label: 'Feedback', enabled: true, order: 6 },
  ];
  const [statusUpdateScreens, setStatusUpdateScreens] = useState(DEFAULT_STATUS_UPDATE_SCREENS);
  const [statusUpdateScreensLoading, setStatusUpdateScreensLoading] = useState(false);
  const [statusUpdateScreensSaving, setStatusUpdateScreensSaving] = useState(false);
  const [statusUpdateScreensError, setStatusUpdateScreensError] = useState('');
  const [statusUpdateDragIndex, setStatusUpdateDragIndex] = useState<number | null>(null);
  const [statusUpdateDragOverIndex, setStatusUpdateDragOverIndex] = useState<number | null>(null);

  // Tasks state
  const [newTask, setNewTask] = useState('');
  const [tasksWithIds, setTasksWithIds] = useState<Task[]>([]);

  // Feedbacks state
  const [newFeedback, setNewFeedback] = useState('');
  const [feedbacksWithIds, setFeedbacksWithIds] = useState<Feedback[]>([]);

  // Operation Types state
  const [allOperationTypes, setAllOperationTypes] = useState<OperationType[]>([]);
  const [orgOperationTypes, setOrgOperationTypes] = useState<OperationType[]>([]);

  // Role editing state
  const [roleMembers, setRoleMembers] = useState<any[]>([]);
  const [rolePermissions, setRolePermissions] = useState<any[]>([]);
  const [loadingRoleDetails, setLoadingRoleDetails] = useState(false);

  // User management in roles state
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUserID, setSelectedUserID] = useState('');

  // CSV Import state
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResults, setCsvResults] = useState<{ success: number; errors: { row: number; name: string; error: string }[] } | null>(null);
  const [csvPreview, setCsvPreview] = useState<any[]>([]);
  const csvFileInputRef = React.useRef<HTMLInputElement>(null);

  // Check permissions
  const hasSuperAdminPermission = user?.permissions?.['firehub-super-admin'];
  const hasAsmSettingsPermission = user?.permissions?.['asm-settings-permission'];
  const hasAsmManageUsers = user?.permissions?.['asm-manage-users'];
  const hasAsmManageRoles = user?.permissions?.['asm-manage-roles'];
  
  const canManageUsers = hasSuperAdminPermission || hasAsmManageUsers;
  const canManageRoles = hasSuperAdminPermission || hasAsmManageRoles;
  const canManageSettings = hasSuperAdminPermission || hasAsmSettingsPermission;
  
  // Overall admin access
  const hasAdminAccess = hasSuperAdminPermission || hasAsmSettingsPermission || hasAsmManageUsers || hasAsmManageRoles;

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.orgID) return;

      // Fetch users if user has manage-users permission
      if (canManageUsers && users.length === 0) {
        setLoading(prev => ({ ...prev, users: true }));
        try {
          const usersData = await adminService.getUsers(user.orgID);
          setUsers(usersData);
          setError(prev => ({ ...prev, users: '' }));
        } catch (error: any) {
          setError(prev => ({ ...prev, users: error.message }));
        } finally {
          setLoading(prev => ({ ...prev, users: false }));
        }
      }

      // Fetch roles if user has manage-users permission
      if (canManageUsers && roles.length === 0) {
        setLoading(prev => ({ ...prev, roles: true }));
        try {
          const rolesData = await adminService.getRoles(user.orgID);
          setRoles(rolesData);
          setError(prev => ({ ...prev, roles: '' }));
        } catch (error: any) {
          setError(prev => ({ ...prev, roles: error.message }));
        } finally {
          setLoading(prev => ({ ...prev, roles: false }));
        }
      }

      // Fetch ranks if user has manage-users permission
      if (canManageUsers && ranks.length === 0) {
        setLoading(prev => ({ ...prev, ranks: true }));
        try {
          const ranksData = await adminService.getRanks();
          setRanks(ranksData);
          setError(prev => ({ ...prev, ranks: '' }));
        } catch (error: any) {
          setError(prev => ({ ...prev, ranks: error.message }));
        } finally {
          setLoading(prev => ({ ...prev, ranks: false }));
        }
      }

      // Fetch locations if user has manage-settings permission
      if (canManageSettings && locationsWithIds.length === 0) {
        setLoading(prev => ({ ...prev, locations: true }));
        try {
          const locationsData = await adminService.getLocations(user.orgID);
          setLocationsWithIds(locationsData);
          setStandardStandorte(locationsData.map(location => location.name));
          setError(prev => ({ ...prev, locations: '' }));
        } catch (error: any) {
          setError(prev => ({ ...prev, locations: error.message }));
        } finally {
          setLoading(prev => ({ ...prev, locations: false }));
        }
      }

      // Fetch radio channels if user has manage-settings permission
      if (canManageSettings && radioChannelsWithIds.length === 0) {
        setLoading(prev => ({ ...prev, radioChannels: true }));
        try {
          const radioChannelsData = await adminService.getRadioChannels(user.orgID);
          setRadioChannelsWithIds(radioChannelsData);
          setStandardFunkkanaele(radioChannelsData.map(channel => channel.name));
          setError(prev => ({ ...prev, radioChannels: '' }));
        } catch (error: any) {
          setError(prev => ({ ...prev, radioChannels: error.message }));
        } finally {
          setLoading(prev => ({ ...prev, radioChannels: false }));
        }
      }

      // Fetch tasks if user has manage-settings permission
      if (canManageSettings && tasksWithIds.length === 0) {
        setLoading(prev => ({ ...prev, tasks: true }));
        try {
          const tasksData = await adminService.getTasks(user.orgID);
          setTasksWithIds(tasksData);
          setError(prev => ({ ...prev, tasks: '' }));
        } catch (error: any) {
          setError(prev => ({ ...prev, tasks: error.message }));
        } finally {
          setLoading(prev => ({ ...prev, tasks: false }));
        }
      }

      // Fetch feedbacks if user has manage-settings permission
      if (canManageSettings && feedbacksWithIds.length === 0) {
        try {
          const feedbacksData = await adminService.getFeedbacks(user.orgID);
          setFeedbacksWithIds(feedbacksData);
        } catch (error: any) {
          console.error('Error loading feedbacks:', error);
        }
      }

      // Fetch operation types if user has manage-settings permission
      if (canManageSettings && allOperationTypes.length === 0) {
        setLoading(prev => ({ ...prev, operationTypes: true }));
        try {
          // Get all available operation types from database (like syncAllOperationsTypes in PWA)
          const allTypesData = await adminService.getAllOperationTypes();
          // Get organization's assigned operation types (like syncOperationsTypes in PWA)
          const orgTypesData = await adminService.getOperationTypesByOrg(user.orgID);
          

          
          // Mark which types are assigned to the organization
          let typesWithAssignmentStatus = allTypesData.map(type => ({
            ...type,
            assigned: orgTypesData.some(orgType => orgType.id === type.id)
          }));
          

          
          setAllOperationTypes(typesWithAssignmentStatus);
          setOrgOperationTypes(orgTypesData);
          setError(prev => ({ ...prev, operationTypes: '' }));
        } catch (error: any) {
          setError(prev => ({ ...prev, operationTypes: error.message }));
        } finally {
          setLoading(prev => ({ ...prev, operationTypes: false }));
        }
      }
    };

    fetchData();
  }, [user?.orgID, canManageUsers, canManageSettings]);

  // Load quick trupp creation setting
  useEffect(() => {
    const loadQuickTruppSetting = async () => {
      if (!user?.orgID) return;
      
      try {
        const response = await api.get(`/api/v1/settings/quick-trupp-creation?orgID=${user.orgID}`);
        console.log('🔧 Quick Trupp Creation API Response:', response.data);
        console.log('🔧 data.enabled:', response.data.enabled, 'type:', typeof response.data.enabled);
        setQuickTruppCreation(response.data.enabled || false);
      } catch (error: any) {
        console.error('Error loading quick trupp creation setting:', error);
        console.log('🔧 Error details:', error.response?.data);
        console.log('🔧 Error status:', error.response?.status);
        // Set default value on error
        setQuickTruppCreation(false);
      }
    };

    loadQuickTruppSetting();
  }, [user?.orgID]);

  // Save quick trupp creation setting
  const saveQuickTruppSetting = async (enabled: boolean) => {
    if (!user?.orgID) return;
    
    try {
      const response = await api.post('/api/v1/settings/quick-trupp-creation', { 
        orgID: user.orgID,
        enabled: enabled
      });
      
      setQuickTruppCreation(enabled);
      console.log('Quick trupp creation setting saved:', enabled);
    } catch (error) {
      console.error('Error saving quick trupp creation setting:', error);
    }
  };

  useEffect(() => {
    const loadDeferredAtemschutzSetting = async () => {
      if (!user?.orgID) return;
      try {
        const response = await api.get(`/api/v1/settings/deferred-atemschutz?orgID=${user.orgID}`);
        setDeferredAtemschutz(response.data.enabled || false);
      } catch (error) {
        console.error('Error loading deferred atemschutz setting:', error);
        setDeferredAtemschutz(false);
      }
    };
    loadDeferredAtemschutzSetting();
  }, [user?.orgID]);

  const saveDeferredAtemschutzSetting = async (enabled: boolean) => {
    if (!user?.orgID) return;
    try {
      await api.post('/api/v1/settings/deferred-atemschutz', {
        orgID: user.orgID,
        enabled,
      });
      setDeferredAtemschutz(enabled);
    } catch (error) {
      console.error('Error saving deferred atemschutz setting:', error);
    }
  };

  // Load trupp naming mode setting
  useEffect(() => {
    const loadTruppNamingMode = async () => {
      if (!user?.orgID) return;
      try {
        setTruppNamingModeLoading(true);
        const response = await api.get(`/api/v1/settings/trupp-naming-mode?orgID=${user.orgID}`);
        const mode = response.data?.mode === 'number' ? 'number' : 'leader';
        setTruppNamingMode(mode);
        localStorage.setItem(`truppNamingMode_${user.orgID}`, mode);
        localStorage.setItem('truppNamingMode', mode);
      } catch (error) {
        console.error('Error loading trupp naming mode setting:', error);
      } finally {
        setTruppNamingModeLoading(false);
      }
    };
    loadTruppNamingMode();
  }, [user?.orgID]);

  const saveTruppNamingMode = async (mode: 'leader' | 'number') => {
    if (!user?.orgID) return;
    const orgID = user.orgID;
    const previousMode = truppNamingMode;

    setTruppNamingMode(mode);
    localStorage.setItem(`truppNamingMode_${orgID}`, mode);
    localStorage.setItem('truppNamingMode', mode);
    window.dispatchEvent(new CustomEvent('truppNamingModeChanged', { detail: { mode, orgID } }));

    try {
      setTruppNamingModeLoading(true);
      await api.post('/api/v1/settings/trupp-naming-mode', { orgID, mode });
    } catch (error) {
      console.error('Failed to update trupp naming mode setting:', error);
      const fallbackMode: 'leader' | 'number' = previousMode;
      setTruppNamingMode(fallbackMode);
      localStorage.setItem(`truppNamingMode_${orgID}`, fallbackMode);
      localStorage.setItem('truppNamingMode', fallbackMode);
      window.dispatchEvent(new CustomEvent('truppNamingModeChanged', { detail: { mode: fallbackMode, orgID } }));
    } finally {
      setTruppNamingModeLoading(false);
    }
  };

  // Load org short name + prefix setting
  useEffect(() => {
    const loadOrgPrefixSettings = async () => {
      if (!user?.orgID) return;
      try {
        setOrgShortNameLoading(true);
        setTruppNamePrefixLoading(true);
        const [shortRes, prefixRes] = await Promise.all([
          api.get(`/api/v1/settings/org-short-name?orgID=${user.orgID}`),
          api.get(`/api/v1/settings/trupp-name-prefix?orgID=${user.orgID}`),
        ]);
        const shortName = String(shortRes.data?.shortName ?? '').trim();
        const enabled = prefixRes.data?.enabled === true;
        setOrgShortName(shortName);
        setOrgShortNameDraft(shortName);
        setTruppNamePrefixEnabled(enabled);
        localStorage.setItem(`orgShortName_${user.orgID}`, shortName);
        localStorage.setItem(`truppNamePrefixEnabled_${user.orgID}`, enabled.toString());
        localStorage.setItem('orgShortName', shortName);
        localStorage.setItem('truppNamePrefixEnabled', enabled.toString());
      } catch (error) {
        console.error('Error loading org prefix settings:', error);
      } finally {
        setOrgShortNameLoading(false);
        setTruppNamePrefixLoading(false);
      }
    };
    loadOrgPrefixSettings();
  }, [user?.orgID]);

  const saveOrgShortName = async () => {
    if (!user?.orgID) return;
    const orgID = user.orgID;
    const next = orgShortNameDraft.trim();
    if (next.length > 10) {
      setOrgShortNameError('Maximal 10 Zeichen');
      return;
    }
    if (next && !/^[A-Za-z0-9._\-]+$/.test(next)) {
      setOrgShortNameError('Nur Buchstaben, Ziffern sowie . _ - (ohne Leerzeichen)');
      return;
    }
    const previous = orgShortName;
    setOrgShortName(next);
    setOrgShortNameError('');
    localStorage.setItem(`orgShortName_${orgID}`, next);
    localStorage.setItem('orgShortName', next);
    window.dispatchEvent(new CustomEvent('orgShortNameChanged', { detail: { shortName: next, orgID } }));

    try {
      setOrgShortNameSaving(true);
      const response = await api.post('/api/v1/settings/org-short-name', { orgID, shortName: next });
      const saved = String(response.data?.shortName ?? next).trim();
      setOrgShortName(saved);
      setOrgShortNameDraft(saved);
      localStorage.setItem(`orgShortName_${orgID}`, saved);
      localStorage.setItem('orgShortName', saved);
    } catch (error: any) {
      console.error('Failed to update org short name:', error);
      setOrgShortName(previous);
      setOrgShortNameDraft(previous);
      localStorage.setItem(`orgShortName_${orgID}`, previous);
      localStorage.setItem('orgShortName', previous);
      window.dispatchEvent(new CustomEvent('orgShortNameChanged', { detail: { shortName: previous, orgID } }));
      setOrgShortNameError(error?.response?.data?.error || error?.message || 'Speichern fehlgeschlagen');
    } finally {
      setOrgShortNameSaving(false);
    }
  };

  const saveTruppNamePrefixEnabled = async (enabled: boolean) => {
    if (!user?.orgID) return;
    const orgID = user.orgID;
    const previous = truppNamePrefixEnabled;
    setTruppNamePrefixEnabled(enabled);
    localStorage.setItem(`truppNamePrefixEnabled_${orgID}`, enabled.toString());
    localStorage.setItem('truppNamePrefixEnabled', enabled.toString());
    window.dispatchEvent(new CustomEvent('truppNamePrefixChanged', { detail: { enabled, orgID } }));

    try {
      setTruppNamePrefixLoading(true);
      await api.post('/api/v1/settings/trupp-name-prefix', { orgID, enabled });
    } catch (error) {
      console.error('Failed to update trupp name prefix setting:', error);
      setTruppNamePrefixEnabled(previous);
      localStorage.setItem(`truppNamePrefixEnabled_${orgID}`, previous.toString());
      localStorage.setItem('truppNamePrefixEnabled', previous.toString());
      window.dispatchEvent(new CustomEvent('truppNamePrefixChanged', { detail: { enabled: previous, orgID } }));
    } finally {
      setTruppNamePrefixLoading(false);
    }
  };

  // Load status update screens setting
  useEffect(() => {
    const loadStatusUpdateScreens = async () => {
      if (!user?.orgID) return;
      try {
        setStatusUpdateScreensLoading(true);
        const response = await api.get(`/api/v1/settings/status-update-screens?orgID=${user.orgID}`);
        const screens = Array.isArray(response.data?.screens) ? response.data.screens : DEFAULT_STATUS_UPDATE_SCREENS;
        setStatusUpdateScreens(screens);
        localStorage.setItem(`statusUpdateScreens_${user.orgID}`, JSON.stringify(screens));
      } catch (error) {
        console.error('Error loading status update screens setting:', error);
        setStatusUpdateScreens(DEFAULT_STATUS_UPDATE_SCREENS);
      } finally {
        setStatusUpdateScreensLoading(false);
      }
    };
    loadStatusUpdateScreens();
  }, [user?.orgID]);

  const saveStatusUpdateScreens = async (screens: typeof DEFAULT_STATUS_UPDATE_SCREENS) => {
    if (!user?.orgID) return;
    const orgID = user.orgID;
    const previous = statusUpdateScreens;
    const normalized = screens.map((s, index) => ({ ...s, order: index + 1 }));
    setStatusUpdateScreens(normalized);
    setStatusUpdateScreensError('');
    localStorage.setItem(`statusUpdateScreens_${orgID}`, JSON.stringify(normalized));

    try {
      setStatusUpdateScreensSaving(true);
      const response = await api.post('/api/v1/settings/status-update-screens', {
        orgID,
        screens: normalized
      });
      const saved = Array.isArray(response.data?.screens) ? response.data.screens : normalized;
      setStatusUpdateScreens(saved);
      localStorage.setItem(`statusUpdateScreens_${orgID}`, JSON.stringify(saved));
    } catch (error: any) {
      console.error('Failed to update status update screens setting:', error);
      setStatusUpdateScreens(previous);
      localStorage.setItem(`statusUpdateScreens_${orgID}`, JSON.stringify(previous));
      const data = error?.response?.data;
      const candidates = [
        data?.error?.message,
        data?.details,
        typeof data?.error === 'string' ? data.error : null,
        data?.message,
        error?.message,
      ];
      const message = candidates.find((c) => typeof c === 'string' && c.trim())
        || (data ? JSON.stringify(data) : 'Speichern fehlgeschlagen');
      setStatusUpdateScreensError(message);
    } finally {
      setStatusUpdateScreensSaving(false);
    }
  };

  const reorderStatusUpdateScreens = (fromIndex: number, toIndex: number) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= statusUpdateScreens.length ||
      toIndex >= statusUpdateScreens.length
    ) {
      return;
    }
    const next = [...statusUpdateScreens];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    void saveStatusUpdateScreens(next);
  };

  const toggleStatusUpdateScreen = (index: number) => {
    const enabledCount = statusUpdateScreens.filter((s) => s.enabled).length;
    const current = statusUpdateScreens[index];
    if (current.enabled && enabledCount <= 1) return;
    const next = statusUpdateScreens.map((s, i) => i === index ? { ...s, enabled: !s.enabled } : s);
    void saveStatusUpdateScreens(next);
  };

  // If user has no admin permissions, show access denied
  if (!hasAdminAccess) {
    return (
      <div className="portal-card" style={{ textAlign: 'center', maxWidth: 520, margin: '40px auto' }}>
        <h2 className="portal-page-title">Zugriff verweigert</h2>
        <p className="portal-page-subtitle" style={{ margin: '10px auto 0' }}>
          Sie haben keine Berechtigung, auf den Admin-Bereich zuzugreifen.
        </p>
        <ul style={{ textAlign: 'left', display: 'inline-block', margin: '16px 0 0', color: '#555', fontSize: 14 }}>
          <li>FireHub Super Admin: {user?.permissions?.['firehub-super-admin'] ? '✅' : '❌'}</li>
          <li>ASM Settings Permission: {user?.permissions?.['asm-settings-permission'] ? '✅' : '❌'}</li>
          <li>ASM Manage Users: {user?.permissions?.['asm-manage-users'] ? '✅' : '❌'}</li>
          <li>ASM Manage Roles: {user?.permissions?.['asm-manage-roles'] ? '✅' : '❌'}</li>
        </ul>
      </div>
    );
  }

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!userSearchTerm) return true;
    const searchLower = userSearchTerm.toLowerCase();
    return (
      user.username?.toLowerCase().includes(searchLower) ||
      user.rankShort?.toLowerCase().includes(searchLower) ||
      user.lastname?.toLowerCase().includes(searchLower) ||
      user.firstname?.toLowerCase().includes(searchLower)
    );
  });

  // Dynamic tabs based on permissions
  const getTabs = () => {
    const tabs = [];
    
    if (canManageUsers) {
      tabs.push(
        { label: 'Benutzer', content: 'users', icon: faUsers }
      );
    }
    
    if (canManageRoles) {
      tabs.push(
        { label: 'Rollen', content: 'roles', icon: faUserTie }
      );
    }
    
    if (canManageSettings) {
      tabs.push(
        { label: 'Einstellungen', content: 'settings', icon: faCog }
      );
    }
    
    return tabs;
  };

  const tabs = getTabs();

  const handleOpenDialog = (type: 'user' | 'location' | 'channel' | 'supervisor' | 'role', item?: any) => {
    setDialogType(type);
    setEditingItem(item || null);
    
    // Initialize form data for user editing
    if (type === 'user' && item) {
      setFormData({
        firstName: item.firstname || '',
        lastName: item.lastname || '',
        email: item.email || '',
        telNumber: item.telNumber || '',
        rankID: item.rankID?.toString() || '',
        password: '',
        typeID: item.typeID?.toString() || '1',
      });
    } else if (type === 'user') {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        telNumber: '',
        rankID: '',
        password: '',
        typeID: '1',
      });
    }

    // Initialize form data for role editing
    if (type === 'role' && item) {
      setRoleFormData({
        name: item.name || '',
        nameShort: item.nameShort || 'xxx',
        description: item.description || ''
      });
    } else if (type === 'role') {
      setRoleFormData({
        name: '',
        nameShort: 'xxx',
        description: ''
      });
    }

    // Load role details for role editing
    if (type === 'role' && item && user?.orgID) {
      loadRoleDetails(item.id, user.orgID, item.permissions, item.name);
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    // Reset role form data
    setRoleFormData({
      name: '',
      nameShort: 'xxx',
      description: ''
    });
  };

  const handleSave = async () => {
    if (dialogType === 'user' && editingItem) {
      try {
        const userData: any = {
          orgID: user?.orgID || 0,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          telNumber: formData.telNumber.trim() || null,
          rankID: formData.rankID ? parseInt(formData.rankID) : undefined,
          typeID: parseInt(formData.typeID)
        };

        // Add password only if it was entered (for password changes)
        if (formData.password && formData.password.trim() !== '') {
          userData.password = formData.password;
        }

        // Validate required fields
        if (!userData.firstName || !userData.lastName || !userData.email) {
          console.error('Validation failed: Required fields missing');
          return;
        }

        // Update user via API
        await adminService.updateUser(editingItem.id, userData);
        
        // Refresh users list
        if (user?.orgID) {
          const updatedUsers = await adminService.getUsers(user.orgID);
          setUsers(updatedUsers);
        }

        handleCloseDialog();
      } catch (error: any) {
        console.error('Error updating user:', error);
      }
    } else if (dialogType === 'user' && !editingItem) {
      try {
        
        // Validate required fields for new user
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.rankID) {
          console.error('Validation failed: Required fields missing for new user');
          alert('Bitte füllen Sie alle erforderlichen Felder aus.');
          return;
        }

        const newUserData = {
          orgID: user?.orgID || 0,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          telNumber: formData.telNumber.trim() || null,
          showInContacts: 1,
          password: formData.password,
          rankID: parseInt(formData.rankID),
          typeID: parseInt(formData.typeID),
          languageID: 1, // Deutsch
          truckID: null // Kein Fahrzeug
        };

        if (user?.orgID) {
          const newUser = await adminService.addUser(newUserData);
          
          // Refresh users list
          const updatedUsers = await adminService.getUsers(user.orgID);
          setUsers(updatedUsers);
        }
        
        handleCloseDialog();
      } catch (error: any) {
        console.error('Error creating user:', error);
        alert(`Fehler beim Erstellen des Benutzers: ${error.message}`);
      }
    } else if (dialogType === 'role' && editingItem) {
      try {
        // Update role details
        if (user?.orgID) {
          // Try API update first, but don't fail if it doesn't work
          try {
            await adminService.updateRole(editingItem.id, user.orgID, {
              name: roleFormData.name,
              nameShort: roleFormData.nameShort,
              description: roleFormData.description
            });
            console.log('Role updated via API successfully');
          } catch (apiError: any) {
            console.warn('API update failed, using local update:', apiError.message);
            
            // Fallback: Update locally
            setRoles(prevRoles => 
              prevRoles.map(role => 
                role.id === editingItem.id 
                  ? { 
                      ...role, 
                      name: roleFormData.name,
                      nameShort: roleFormData.nameShort,
                      description: roleFormData.description
                    }
                  : role
              )
            );
          }
          
          // Always try to refresh from backend
          try {
            const updatedRoles = await adminService.getRoles(user.orgID);
            setRoles(updatedRoles);
          } catch (refreshError) {
            console.warn('Could not refresh roles from backend:', refreshError);
          }
        }
        
        handleCloseDialog();
      } catch (error: any) {
        console.error('Error updating role:', error);
        alert(`Fehler beim Aktualisieren der Rolle: ${error.message}`);
      }
    } else if (dialogType === 'role' && !editingItem) {
      try {
        
        if (!roleFormData.name || !roleFormData.description) {
          console.error('Validation failed: Required role fields missing');
          return;
        }

        if (user?.orgID) {
          const newRole = await adminService.createRole(user.orgID, roleFormData);
          
          // Refresh roles list
          const updatedRoles = await adminService.getRoles(user.orgID);
          setRoles(updatedRoles);
        }
        
        handleCloseDialog();
      } catch (error: any) {
        console.error('Error creating role:', error);
      }
    } else {
      // Mock save functionality for other types
      handleCloseDialog();
    }
  };

  const handleDelete = async (type: string, id: number, name?: string) => {
    // Open delete confirmation modal
    setDeleteModal({
      open: true,
      type,
      id,
      name: name || `Eintrag mit ID ${id}`
    });
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteModal;
    
    if (type === 'users') {
      try {
        await adminService.deleteUser(id, user?.orgID || 0);
        
        // Refresh users list
        if (user?.orgID) {
          const updatedUsers = await adminService.getUsers(user.orgID);
          setUsers(updatedUsers);
        }
      } catch (error: any) {
        console.error('Error deleting user:', error);
      }
    } else if (type === 'roles') {
      try {
        await adminService.deleteRole(id, user?.orgID || 0);
        
        // Refresh roles list
        if (user?.orgID) {
          const updatedRoles = await adminService.getRoles(user.orgID);
          setRoles(updatedRoles);
        }
      } catch (error: any) {
        console.error('Error deleting role:', error);
      }
    }
    
    // Close modal
    setDeleteModal({ open: false, type: '', id: 0, name: '' });
  };

  const handleCancelDelete = () => {
    setDeleteModal({ open: false, type: '', id: 0, name: '' });
  };

  const loadRoleDetails = async (roleID: number, orgID: number, permissions?: any[], roleName?: string) => {
    try {
      setLoadingRoleDetails(true);
      
      // Reset data
      setRoleMembers([]);
      setRolePermissions([]);
      setAvailableUsers([]);
      setSelectedUserID('');
      
      // Load role members
      const members = await adminService.getRoleUsers(orgID, roleID);
      setRoleMembers(Array.isArray(members) ? members : []);
      
      // Load available users (all users minus current members)
      if (user?.orgID) {
        const allUsers = await adminService.getUsers(user.orgID);
        const memberIDs = Array.isArray(members) ? members.map(m => m.id) : [];
        const available = allUsers.filter(u => !memberIDs.includes(u.id));
        setAvailableUsers(available);
      }
      
      if (permissions) {
        
        // Fix assigned status based on role type
        const fixedPermissions = permissions.map(permission => {
          // For FireHub Super Admin role, all permissions should be assigned
          if (roleName === 'FireHub Super Admin') {
            return { ...permission, assigned: true };
          }
          // For other roles, use the assigned status from API
          return permission;
        });
        
        setRolePermissions(Array.isArray(fixedPermissions) ? fixedPermissions : []);
      } else {
        setRolePermissions([]);
      }
      
    } catch (error: any) {
      console.error('Error loading role details:', error);
      // Reset on error
      setRoleMembers([]);
      setRolePermissions([]);
      setAvailableUsers([]);
    } finally {
      setLoadingRoleDetails(false);
    }
  };

  // Settings functions
  const saveStandardFunkkanaele = async () => {
    if (newFunkkanal.trim() && user?.orgID) {
      try {
        setLoading(prev => ({ ...prev, radioChannels: true }));
        const result = await adminService.addRadioChannel(user.orgID, newFunkkanal.trim(), user.id);
        
        // Refresh radio channels list
        const updatedChannels = await adminService.getRadioChannels(user.orgID);
        setRadioChannelsWithIds(updatedChannels);
        setStandardFunkkanaele(updatedChannels.map(channel => channel.name));
        setNewFunkkanal('');
      } catch (error: any) {
        console.error('Error adding radio channel:', error);
      } finally {
        setLoading(prev => ({ ...prev, radioChannels: false }));
      }
    }
  };

  const deleteRadioChannel = async (id: number, name: string) => {
    try {
      setLoading(prev => ({ ...prev, radioChannels: true }));
      await adminService.deleteRadioChannel(id, user?.orgID || 0);
      
      // Refresh radio channels list
      if (user?.orgID) {
        const updatedChannels = await adminService.getRadioChannels(user.orgID);
        setRadioChannelsWithIds(updatedChannels);
        setStandardFunkkanaele(updatedChannels.map(channel => channel.name));
      }
    } catch (error: any) {
      console.error('Error deleting radio channel:', error);
    } finally {
      setLoading(prev => ({ ...prev, radioChannels: false }));
    }
  };

  const saveStandardStandorte = async () => {
    if (newStandort.trim() && user?.orgID) {
      try {
        setLoading(prev => ({ ...prev, locations: true }));
        const result = await adminService.addLocation(user.orgID, newStandort.trim(), user.id);
        
        // Refresh locations list
        const updatedLocations = await adminService.getLocations(user.orgID);
        setLocationsWithIds(updatedLocations);
        setStandardStandorte(updatedLocations.map(location => location.name));
        setNewStandort('');
      } catch (error: any) {
        console.error('Error adding location:', error);
      } finally {
        setLoading(prev => ({ ...prev, locations: false }));
      }
    }
  };

  const deleteLocation = async (id: number, name: string) => {
    try {
      setLoading(prev => ({ ...prev, locations: true }));
      await adminService.deleteLocation(id, user?.orgID || 0);
      
      // Refresh locations list
      if (user?.orgID) {
        const updatedLocations = await adminService.getLocations(user.orgID);
        setLocationsWithIds(updatedLocations);
        setStandardStandorte(updatedLocations.map(location => location.name));
      }
    } catch (error: any) {
      console.error('Error deleting location:', error);
    } finally {
      setLoading(prev => ({ ...prev, locations: false }));
    }
  };

  // Tasks functions
  const saveStandardTasks = async () => {
    if (newTask.trim() && user?.orgID) {
      try {
        setLoading(prev => ({ ...prev, tasks: true }));
        const result = await adminService.addTask(user.orgID, newTask.trim());
        
        // Refresh tasks list
        const updatedTasks = await adminService.getTasks(user.orgID);
        setTasksWithIds(updatedTasks);
        setNewTask('');
      } catch (error: any) {
        console.error('Error adding task:', error);
      } finally {
        setLoading(prev => ({ ...prev, tasks: false }));
      }
    }
  };

  const deleteTask = async (id: number, name: string) => {
    try {
      setLoading(prev => ({ ...prev, tasks: true }));
      await adminService.deleteTask(id, user?.orgID || 0);
      
      // Refresh tasks list
      if (user?.orgID) {
        const updatedTasks = await adminService.getTasks(user.orgID);
        setTasksWithIds(updatedTasks);
      }
    } catch (error: any) {
      console.error('Error deleting task:', error);
    } finally {
      setLoading(prev => ({ ...prev, tasks: false }));
    }
  };

  // Feedbacks functions
  const saveStandardFeedbacks = async () => {
    if (newFeedback.trim() && user?.orgID) {
      try {
        await adminService.addFeedback(user.orgID, newFeedback.trim(), user.id);
        const updatedFeedbacks = await adminService.getFeedbacks(user.orgID);
        setFeedbacksWithIds(updatedFeedbacks);
        setNewFeedback('');
      } catch (error: any) {
        console.error('Error adding feedback:', error);
      }
    }
  };

  const deleteFeedback = async (id: number, text: string) => {
    try {
      await adminService.deleteFeedback(id, user?.orgID || 0);
      if (user?.orgID) {
        const updatedFeedbacks = await adminService.getFeedbacks(user.orgID);
        setFeedbacksWithIds(updatedFeedbacks);
      }
    } catch (error: any) {
      console.error('Error deleting feedback:', error);
    }
  };

  // Operation Types functions (following PWA pattern)
  const addOperationTypeToOrg = async (opsTypeID: number) => {
    if (user?.orgID) {
      try {
        setLoading(prev => ({ ...prev, operationTypes: true }));
        await adminService.addOperationTypeToOrg(user.orgID, opsTypeID);
        
        // Refresh organization's assigned types (like in PWA)
        const orgTypesData = await adminService.getOperationTypesByOrg(user.orgID);
        setOrgOperationTypes(orgTypesData);
        
        // Update all types with new assignment status
        setAllOperationTypes(prev => 
          prev.map(type => ({
            ...type,
            assigned: orgTypesData.some(orgType => orgType.id === type.id)
          }))
        );
      } catch (error: any) {
        console.error('Error adding operation type to org:', error);
      } finally {
        setLoading(prev => ({ ...prev, operationTypes: false }));
      }
    }
  };

  const removeOperationTypeFromOrg = async (opsTypeID: number) => {
    if (user?.orgID) {
      try {
        setLoading(prev => ({ ...prev, operationTypes: true }));
        await adminService.removeOperationTypeFromOrg(user.orgID, opsTypeID);
        
        // Refresh organization's assigned types (like in PWA)
        const orgTypesData = await adminService.getOperationTypesByOrg(user.orgID);
        setOrgOperationTypes(orgTypesData);
        
        // Update all types with new assignment status
        setAllOperationTypes(prev => 
          prev.map(type => ({
            ...type,
            assigned: orgTypesData.some(orgType => orgType.id === type.id)
          }))
        );
      } catch (error: any) {
        console.error('Error removing operation type from org:', error);
      } finally {
        setLoading(prev => ({ ...prev, operationTypes: false }));
      }
    }
  };

  // CSV Import functions
  const generateSampleCsv = () => {
    const header = 'Vorname;Nachname;E-Mail;Telefon;Passwort;RangID;Kontotyp';
    const rows = [
      'Max;Mustermann;max.mustermann@example.com;+41791234567;Passwort123!;1;1',
      'Erika;Musterfrau;erika.musterfrau@example.com;;Passwort456!;2;1',
      'Tech;Account;tech@example.com;;TechPass789!;1;2',
    ];
    const comment = [
      '',
      '# Hinweise:',
      '# Trennzeichen: Semikolon (;)',
      '# Kontotyp: 1 = Normaler Benutzer / 2 = Technischer Benutzer',
      '# Telefon: optional',
      '# RangID: Entspricht der ID des gewünschten Ranges (siehe Admin > Benutzer > Rang-Dropdown)',
      '# Verfügbare Ränge und deren IDs:',
      ...ranks.map(r => `#   ${r.id} = ${r.descLong || r.name}`),
    ];
    const csvContent = [header, ...rows, ...comment].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'benutzer_import_vorlage.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const parseCsvFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split(/\r?\n/).filter(line => line.trim() && !line.trim().startsWith('#'));
          if (lines.length < 2) {
            reject(new Error('Die CSV-Datei enthält keine Datenzeilen.'));
            return;
          }
          const header = lines[0].split(';').map(h => h.trim());
          const expectedHeaders = ['Vorname', 'Nachname', 'E-Mail', 'Passwort', 'RangID', 'Kontotyp'];
          const headerValid = expectedHeaders.every(eh => header.includes(eh));
          if (!headerValid) {
            reject(new Error(`Ungültige Spalten. Erwartet: ${expectedHeaders.join('; ')} (optional: Telefon)`));
            return;
          }
          const data = lines.slice(1).map((line, idx) => {
            const values = line.split(';').map(v => v.trim());
            const row: any = {};
            header.forEach((h, i) => { row[h] = values[i] || ''; });
            row._row = idx + 2; // Original row number (1-based + header)
            return row;
          });
          resolve(data);
        } catch (err) {
          reject(new Error('Fehler beim Lesen der CSV-Datei.'));
        }
      };
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
      reader.readAsText(file, 'UTF-8');
    });
  };

  const handleCsvFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await parseCsvFile(file);
      setCsvPreview(data);
      setCsvResults(null);
    } catch (err: any) {
      alert(err.message);
      setCsvPreview([]);
    }
    // Reset file input so same file can be re-selected
    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
  };

  const handleCsvImport = async () => {
    if (!csvPreview.length || !user?.orgID) return;
    setCsvImporting(true);
    const results = { success: 0, errors: [] as { row: number; name: string; error: string }[] };

    for (const row of csvPreview) {
      const name = `${row['Vorname']} ${row['Nachname']}`;
      try {
        if (!row['Vorname'] || !row['Nachname'] || !row['E-Mail'] || !row['Passwort']) {
          throw new Error('Pflichtfelder fehlen (Vorname, Nachname, E-Mail, Passwort)');
        }
        await adminService.addUser({
          orgID: user.orgID,
          firstName: row['Vorname'],
          lastName: row['Nachname'],
          email: row['E-Mail'],
          telNumber: row['Telefon']?.trim() || null,
          showInContacts: 1,
          password: row['Passwort'],
          rankID: parseInt(row['RangID']) || 1,
          typeID: parseInt(row['Kontotyp']) || 1,
          languageID: 1,
          truckID: null,
        });
        results.success++;
      } catch (err: any) {
        results.errors.push({ row: row._row, name, error: err.message });
      }
    }

    setCsvResults(results);
    setCsvImporting(false);

    // Refresh user list
    if (results.success > 0 && user?.orgID) {
      const updatedUsers = await adminService.getUsers(user.orgID);
      setUsers(updatedUsers);
    }
  };

  const renderTable = (data: any[], type: string) => {
    // Handle settings tab
    if (type === 'settings') {
      return (
        <SettingsPanel
          maxTrupps={maxTrupps}
          setMaxTrupps={setMaxTrupps}
          quickTruppCreation={quickTruppCreation}
          saveQuickTruppSetting={saveQuickTruppSetting}
          deferredAtemschutz={deferredAtemschutz}
          saveDeferredAtemschutzSetting={saveDeferredAtemschutzSetting}
          truppNamingMode={truppNamingMode}
          truppNamingModeLoading={truppNamingModeLoading}
          saveTruppNamingMode={saveTruppNamingMode}
          orgShortName={orgShortName}
          orgShortNameDraft={orgShortNameDraft}
          setOrgShortNameDraft={setOrgShortNameDraft}
          orgShortNameLoading={orgShortNameLoading}
          orgShortNameSaving={orgShortNameSaving}
          orgShortNameError={orgShortNameError}
          setOrgShortNameError={setOrgShortNameError}
          saveOrgShortName={saveOrgShortName}
          truppNamePrefixEnabled={truppNamePrefixEnabled}
          truppNamePrefixLoading={truppNamePrefixLoading}
          saveTruppNamePrefixEnabled={saveTruppNamePrefixEnabled}
          statusUpdateScreens={statusUpdateScreens}
          statusUpdateScreensLoading={statusUpdateScreensLoading}
          statusUpdateScreensSaving={statusUpdateScreensSaving}
          statusUpdateScreensError={statusUpdateScreensError}
          statusUpdateDragIndex={statusUpdateDragIndex}
          statusUpdateDragOverIndex={statusUpdateDragOverIndex}
          setStatusUpdateDragIndex={setStatusUpdateDragIndex}
          setStatusUpdateDragOverIndex={setStatusUpdateDragOverIndex}
          reorderStatusUpdateScreens={reorderStatusUpdateScreens}
          toggleStatusUpdateScreen={toggleStatusUpdateScreen}
          newFunkkanal={newFunkkanal}
          setNewFunkkanal={setNewFunkkanal}
          saveStandardFunkkanaele={saveStandardFunkkanaele}
          radioChannelsWithIds={radioChannelsWithIds}
          deleteRadioChannel={deleteRadioChannel}
          newTask={newTask}
          setNewTask={setNewTask}
          saveStandardTasks={saveStandardTasks}
          tasksWithIds={tasksWithIds}
          deleteTask={deleteTask}
          newFeedback={newFeedback}
          setNewFeedback={setNewFeedback}
          saveStandardFeedbacks={saveStandardFeedbacks}
          feedbacksWithIds={feedbacksWithIds}
          deleteFeedback={deleteFeedback}
          newStandort={newStandort}
          setNewStandort={setNewStandort}
          saveStandardStandorte={saveStandardStandorte}
          locationsWithIds={locationsWithIds}
          deleteLocation={deleteLocation}
          allOperationTypes={allOperationTypes}
          loadingOperationTypes={loading.operationTypes}
          errorOperationTypes={error.operationTypes}
          addOperationTypeToOrg={addOperationTypeToOrg}
          removeOperationTypeFromOrg={removeOperationTypeFromOrg}
        />
      );
    }

    // Show loading state
    if (type === 'users' && loading.users) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#666'
        }}>
          Benutzer werden geladen...
        </div>
      );
    }

    if (type === 'roles' && loading.roles) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#666'
        }}>
          Rollen werden geladen...
        </div>
      );
    }

    // Show error state
    if (type === 'users' && error.users) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#c00',
          backgroundColor: '#fff',
          borderRadius: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #ececec'
        }}>
          <h3>Fehler beim Laden der Benutzer</h3>
          <p>{error.users}</p>
        </div>
      );
    }

    if (type === 'roles' && error.roles) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#c00',
          backgroundColor: '#fff',
          borderRadius: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #ececec'
        }}>
          <h3>Fehler beim Laden der Rollen</h3>
          <p>{error.roles}</p>
        </div>
      );
    }

    const getHeaders = () => {
      switch (type) {
        case 'users':
          return isMobile ? ['Rang', 'Name', 'Aktionen'] : ['Rang', 'Nachname', 'Vorname', 'Benutzername', 'Telefon', 'Kontotyp', 'Aktionen'];
        case 'roles':
          return isMobile ? ['Name', 'Aktionen'] : ['Name', 'Beschreibung', 'Aktionen'];
        case 'locations':
          return ['Name', 'Aktionen'];
        case 'channels':
          return ['Name', 'Frequenz', 'Aktionen'];
        case 'supervisors':
          return ['Name', 'Max. Trupps', 'Aktionen'];
        default:
          return [];
      }
    };

    const getRowData = (item: any) => {
      switch (type) {
        case 'users':
          return isMobile ? [
            item.rankShort || '-',
            `${item.firstname || ''} ${item.lastname || ''}`.trim() || '-',
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleOpenDialog('user', item)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#1976D2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  minWidth: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Bearbeiten"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                onClick={() => handleDelete(type, item.id, `${item.firstname} ${item.lastname}`)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#D32F2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  minWidth: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Löschen"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ] : [
            item.rankShort || '-',
            item.lastname || '-',
            item.firstname || '-',
            item.username,
            item.telNumber || '-',
            <span style={{
              fontSize: '12px',
              padding: '2px 8px',
              borderRadius: '3px',
              fontWeight: 'bold',
              backgroundColor: item.typeID === 2 ? '#e3f2fd' : '#e8f5e9',
              color: item.typeID === 2 ? '#1565c0' : '#2e7d32'
            }}>
              {item.typeID === 2 ? 'Technisch' : 'Normal'}
            </span>,
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleOpenDialog('user', item)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#1976D2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Bearbeiten"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                onClick={() => handleDelete(type, item.id, `${item.firstname} ${item.lastname}`)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#D32F2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Löschen"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ];
        case 'roles':
          return isMobile ? [
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{item.name}</span>
              {item.orgID === 0 && (
                <span style={{ 
                  fontSize: '10px', 
                  background: '#ffc107', 
                  color: '#000', 
                  padding: '2px 6px', 
                  borderRadius: '3px',
                  fontWeight: 'bold'
                }}>
                  SYSTEM
                </span>
              )}
            </div>,
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleOpenDialog('role', item)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#1976D2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Bearbeiten"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                onClick={() => handleDelete(type, item.id)}
                disabled={item.orgID === 0}
                style={{
                  padding: '4px 8px',
                  backgroundColor: item.orgID === 0 ? '#ccc' : '#D32F2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: item.orgID === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  opacity: item.orgID === 0 ? 0.6 : 1
                }}
                title={item.orgID === 0 ? 'System-Rolle kann nicht gelöscht werden' : 'Löschen'}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ] : [
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{item.name}</span>
              {item.orgID === 0 && (
                <span style={{ 
                  fontSize: '10px', 
                  background: '#ffc107', 
                  color: '#000', 
                  padding: '2px 6px', 
                  borderRadius: '3px',
                  fontWeight: 'bold'
                }}>
                  SYSTEM
                </span>
              )}
            </div>,
            item.description,
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleOpenDialog('role', item)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#1976D2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Bearbeiten"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                onClick={() => handleDelete(type, item.id)}
                disabled={item.orgID === 0}
                style={{
                  padding: '4px 8px',
                  backgroundColor: item.orgID === 0 ? '#ccc' : '#D32F2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: item.orgID === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  opacity: item.orgID === 0 ? 0.6 : 1
                }}
                title={item.orgID === 0 ? 'System-Rolle kann nicht gelöscht werden' : 'Löschen'}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ];
        case 'locations':
          return [
            item.name,
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleOpenDialog('location', item)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#1976D2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Bearbeiten"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                onClick={() => handleDelete(type, item.id)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#D32F2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Löschen"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ];
        case 'channels':
          return [
            item.name,
            item.frequency,
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleOpenDialog('channel', item)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#1976D2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Bearbeiten"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                onClick={() => handleDelete(type, item.id)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#D32F2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Löschen"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ];
        case 'supervisors':
          return [
            item.name,
            item.maxTeams,
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleOpenDialog('supervisor', item)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#1976D2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Bearbeiten"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                onClick={() => handleDelete(type, item.id)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#D32F2F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title="Löschen"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ];
        default:
          return [];
      }
    };

    return (
      <div className="portal-table-wrap">
        <table className="portal-table">
          <thead>
            <tr>
              {getHeaders().map((header, index) => (
                <th key={index}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id}>
                {getRowData(item).map((cell, index) => (
                  <td key={index}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const getCurrentData = () => {
    const currentType = tabs[activeTab]?.content;
    if (currentType === 'users') return filteredUsers;
    if (currentType === 'roles') return roles;
    return [];
  };

  const getCurrentType = () => {
    return tabs[activeTab]?.content || 'users';
  };

  const handlePermissionToggle = async (permission: any) => {
    if (!editingItem || !user?.orgID) {
      console.error('Cannot toggle permission: missing editingItem or user.orgID');
      return;
    }

    try {
      
      // Optimistic update - update UI immediately
      const updatedPermissions = rolePermissions.map(p => 
        p.id === permission.id 
          ? { ...p, assigned: !p.assigned }
          : p
      );
      setRolePermissions(updatedPermissions);
      
      // Also update the main roles list for immediate visibility
      const updatedRoles = roles.map(role => 
        role.id === editingItem.id 
          ? { ...role, permissions: updatedPermissions }
          : role
      );
      setRoles(updatedRoles);
      
      // Call API to update the permission
      if (permission.assigned) {
        // Remove permission
        await adminService.removeRolePermission(editingItem.id, user.orgID, permission.id);
      } else {
        // Add permission
        await adminService.addRolePermission(editingItem.id, user.orgID, permission.id);
      }
      
    } catch (error: any) {
      console.error('Error toggling permission:', error);
      
      // Revert optimistic update on error
      const revertedPermissions = rolePermissions.map(p => 
        p.id === permission.id 
          ? { ...p, assigned: permission.assigned } // Revert to original state
          : p
      );
      setRolePermissions(revertedPermissions);
      
      // Also revert the main roles list
      const revertedRoles = roles.map(role => 
        role.id === editingItem.id 
          ? { ...role, permissions: revertedPermissions }
          : role
      );
      setRoles(revertedRoles);
      
      // Show error message to user
      alert(`Fehler beim ${permission.assigned ? 'Entfernen' : 'Hinzufügen'} der Berechtigung: ${error.message}`);
    }
  };

  const handleAddUserToRole = async () => {
    if (!editingItem || !user?.orgID || !selectedUserID) {
      console.error('Cannot add user: missing editingItem, user.orgID, or selectedUserID');
      return;
    }

    try {
      const userID = parseInt(selectedUserID);
      const userToAdd = availableUsers.find(u => u.id === userID);
      
      if (!userToAdd) {
        console.error('Selected user not found in available users');
        return;
      }

      
      // Optimistic update - update UI immediately
      const newMember = {
        id: userToAdd.id,
        firstname: userToAdd.firstname || '',
        lastname: userToAdd.lastname || '',
        username: userToAdd.username,
        rankShort: userToAdd.rankShort || ''
      };
      
      setRoleMembers(prev => [...prev, newMember]);
      setAvailableUsers(prev => prev.filter(u => u.id !== userID));
      setSelectedUserID('');
      
      // Call API to add user to role
      await adminService.addUserToRole(editingItem.id, user.orgID, userID);
      
    } catch (error: any) {
      
      // Revert optimistic update on error
      const userID = parseInt(selectedUserID);
      const userToAdd = availableUsers.find(u => u.id === userID);
      
      if (userToAdd) {
        setRoleMembers(prev => prev.filter(m => m.id !== userID));
        setAvailableUsers(prev => [...prev, userToAdd]);
      }
      setSelectedUserID('');
      
      // Show error message to user
      alert(`Fehler beim Hinzufügen des Benutzers zur Rolle: ${error.message}`);
    }
  };

  const handleRemoveUserFromRole = async (userID: number) => {
    if (!editingItem || !user?.orgID) {
      console.error('Cannot remove user: missing editingItem or user.orgID');
      return;
    }

    try {
      const userToRemove = roleMembers.find(m => m.id === userID);
      
      if (!userToRemove) {
        console.error('User to remove not found in role members');
        return;
      }
      
      // Optimistic update - update UI immediately
      setRoleMembers(prev => prev.filter(m => m.id !== userID));
      setAvailableUsers(prev => [...prev, userToRemove]);
      
      // Call API to remove user from role
      await adminService.removeUserFromRole(editingItem.id, user.orgID, userID);
      
    } catch (error: any) {
      console.error('Error removing user from role:', error);
      
      // Revert optimistic update on error
      const userToRemove = availableUsers.find(u => u.id === userID);
      
      if (userToRemove) {
        setRoleMembers(prev => [...prev, userToRemove]);
        setAvailableUsers(prev => prev.filter(u => u.id !== userID));
      }
      
      // Show error message to user
      alert(`Fehler beim Entfernen des Benutzers aus der Rolle: ${error.message}`);
    }
  };

  return (
    <div className="portal-stack">
      <div className="portal-page-header">
        <div>
          <h1 className="portal-page-title">Admin</h1>
          <p className="portal-page-subtitle">Benutzer, Rollen und Organisationseinstellungen verwalten.</p>
        </div>
      </div>

      <div className="portal-tabs">
        <div className="portal-tabs-bar">
          {tabs.map((tab, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setActiveTab(index)}
              className={`portal-tab${activeTab === index ? ' is-active' : ''}`}
              title={isMobile ? tab.label : undefined}
            >
              <FontAwesomeIcon icon={tab.icon} />
              {!isMobile && tab.label}
            </button>
          ))}
        </div>

        <div className="portal-tabs-body">
          {getCurrentType() !== 'settings' && (
            <div className="portal-page-header" style={{ marginBottom: 16 }}>
              <h3 className="portal-card-title" style={{ margin: 0 }}>{tabs[activeTab]?.label}</h3>
              {(getCurrentType() === 'users' || getCurrentType() === 'roles') && (
                <div className="portal-btn-group">
                  {getCurrentType() === 'users' && (
                    <button
                      type="button"
                      className="portal-btn portal-btn--success"
                      onClick={() => { setCsvImportOpen(true); setCsvPreview([]); setCsvResults(null); }}
                    >
                      <FontAwesomeIcon icon={faFileUpload} />
                      CSV Import
                    </button>
                  )}
                  <button
                    type="button"
                    className="portal-btn portal-btn--primary"
                    onClick={() => {
                      const currentType = getCurrentType();
                      let dialogType;
                      if (currentType === 'users') dialogType = 'user';
                      else if (currentType === 'roles') dialogType = 'role';
                      else dialogType = currentType;
                      handleOpenDialog(dialogType as any);
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Hinzufügen
                  </button>
                </div>
              )}
            </div>
          )}

          {getCurrentType() === 'users' && (
            <div className="portal-card" style={{ marginBottom: 16 }}>
              <div className="portal-filter-field portal-filter-field--grow">
                <label className="portal-label">Suchen</label>
                <input
                  type="text"
                  className="portal-input"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  placeholder="Nach Benutzern suchen…"
                />
              </div>
            </div>
          )}

          {renderTable(getCurrentData(), getCurrentType())}
        </div>
      </div>

      {/* Dialog */}
      {openDialog && (
        <div className="portal-modal-backdrop">
          <div className="portal-modal portal-modal--lg">
            <h2 className="portal-modal-title">
              {editingItem ? 'Bearbeiten' : 'Hinzufügen'}
              {dialogType === 'role' && (editingItem ? ' Rolle' : ' neue Rolle')}
              {dialogType === 'user' && (editingItem ? ' Benutzer' : ' neuen Benutzer')}
            </h2>

            {dialogType === 'user' && (
              <div style={{ minHeight: '200px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold' }}>
                    Rang *
                  </label>
                  <select
                    value={formData.rankID}
                    onChange={(e) => setFormData(prev => ({ ...prev, rankID: e.target.value }))}
                    disabled={loading.ranks}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      opacity: loading.ranks ? 0.6 : 1
                    }}
                  >
                    <option value="">
                      {loading.ranks ? 'Ränge werden geladen...' : 'Rang auswählen...'}
                    </option>
                    {ranks.map((rank) => (
                      <option key={rank.id} value={rank.id}>
                        {rank.descLong || rank.name || 'Unbekannter Rang'}
                      </option>
                    ))}
                  </select>
                  {ranks.length === 0 && !loading.ranks && (
                    <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '5px' }}>
                      Keine Ränge verfügbar
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold' }}>
                    Kontotyp *
                  </label>
                  <select
                    value={formData.typeID}
                    onChange={(e) => setFormData(prev => ({ ...prev, typeID: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="1">Normaler Benutzer</option>
                    <option value="2">Technischer Benutzer</option>
                  </select>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold' }}>
                    Vorname *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Vorname eingeben"
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold' }}>
                    Nachname *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Nachname eingeben"
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold' }}>
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="E-Mail-Adresse eingeben"
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold' }}>
                    Telefonnummer
                  </label>
                  <input
                    type="tel"
                    value={formData.telNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, telNumber: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="z.B. +41791234567"
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold' }}>
                    Passwort {editingItem ? '(leer lassen für keine Änderung)' : '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder={editingItem ? 'Neues Passwort (optional)' : 'Passwort eingeben'}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            )}

            {dialogType === 'location' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#333' }}>
                  Name
                </label>
                <input
                  type="text"
                  defaultValue={editingItem?.name || ''}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}

            {dialogType === 'role' && (
              <div>
                {/* System role warning */}
                {editingItem && editingItem.orgID === 0 && (
                  <div style={{
                    background: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '10px',
                    padding: '12px',
                    marginBottom: '20px',
                    color: '#856404'
                  }}>
                    <strong>System-Rolle:</strong> Diese Rolle kann nicht bearbeitet werden. Nur Benutzer können hinzugefügt/entfernt werden.
                  </div>
                )}
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333' }}>
                    Rollenname *
                  </label>
                  <input
                    type="text"
                    value={roleFormData.name}
                    onChange={(e) => setRoleFormData(prev => ({ ...prev, name: e.target.value }))}
                    disabled={editingItem && editingItem.orgID === 0}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      backgroundColor: editingItem && editingItem.orgID === 0 ? '#f5f5f5' : 'white',
                      cursor: editingItem && editingItem.orgID === 0 ? 'not-allowed' : 'text'
                    }}
                    placeholder="Name der Rolle"
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333' }}>
                    Beschreibung *
                  </label>
                  <input
                    type="text"
                    value={roleFormData.description}
                    onChange={(e) => setRoleFormData(prev => ({ ...prev, description: e.target.value }))}
                    disabled={editingItem && editingItem.orgID === 0}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      backgroundColor: editingItem && editingItem.orgID === 0 ? '#f5f5f5' : 'white',
                      cursor: editingItem && editingItem.orgID === 0 ? 'not-allowed' : 'text'
                    }}
                    placeholder="Beschreibung der Rolle"
                  />
                </div>

                {/* Only show members and permissions for existing roles */}
                {editingItem && (
                  <>
                    {/* Role Members */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '10px', color: '#333', fontWeight: 'bold' }}>
                        Rollenmitglieder ({roleMembers.length})
                      </label>
                      
                      {/* Add user to role */}
                      <div style={{ marginBottom: '15px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <select
                            value={selectedUserID}
                            onChange={(e) => setSelectedUserID(e.target.value)}
                            style={{
                              flex: 1,
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '10px',
                              fontSize: '14px'
                            }}
                          >
                            <option value="">Benutzer auswählen...</option>
                            {availableUsers.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.firstname} {user.lastname} ({user.username})
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={handleAddUserToRole}
                            disabled={!selectedUserID}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: selectedUserID ? '#4CAF50' : '#ccc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              cursor: selectedUserID ? 'pointer' : 'not-allowed',
                              fontSize: '14px'
                            }}
                            title="Benutzer zur Rolle hinzufügen"
                          >
                            <FontAwesomeIcon icon={faPlus} style={{ fontSize: '12px' }} />
                          </button>
                        </div>
                      </div>
                      
                      {loadingRoleDetails ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          Mitglieder werden geladen...
                        </div>
                      ) : (
                        <div style={{ 
                          maxHeight: '200px', 
                          overflowY: 'auto', 
                          border: '1px solid #ddd', 
                          borderRadius: '10px',
                          padding: '10px'
                        }}>
                          {roleMembers.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#999', padding: '10px' }}>
                              Keine Mitglieder in dieser Rolle
                            </div>
                          ) : (
                            roleMembers.map((member, index) => (
                              <div key={member.id || index} style={{ 
                                padding: '8px', 
                                borderBottom: index < roleMembers.length - 1 ? '1px solid #eee' : 'none',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <div>
                                  <div style={{ fontWeight: 'bold' }}>
                                    {member.firstname} {member.lastname}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {member.username} • {member.rankShort}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveUserFromRole(member.id)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    borderRadius: '10px',
                                    color: '#d32f2f',
                                    fontSize: '14px'
                                  }}
                                  title="Benutzer aus Rolle entfernen"
                                >
                                  <FontAwesomeIcon icon={faMinus} style={{ fontSize: '12px' }} />
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {/* Role Permissions */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '10px', color: '#333', fontWeight: 'bold' }}>
                        Berechtigungen ({Array.isArray(rolePermissions) ? rolePermissions.length : 0})
                        {editingItem.orgID === 0 && (
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#6c757d', 
                            fontWeight: 'normal',
                            marginLeft: '8px'
                          }}>
                            (nur anzeigen)
                          </span>
                        )}
                      </label>
                      {loadingRoleDetails ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          Berechtigungen werden geladen...
                        </div>
                      ) : (
                        <div style={{ 
                          maxHeight: '200px', 
                          overflowY: 'auto', 
                          border: '1px solid #ddd', 
                          borderRadius: '10px',
                          padding: '10px'
                        }}>
                          {!Array.isArray(rolePermissions) || rolePermissions.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#999', padding: '10px' }}>
                              Keine Berechtigungen für diese Rolle
                            </div>
                          ) : (
                            rolePermissions
                              .sort((a, b) => {
                                // Sort assigned permissions first (true before false)
                                if (a.assigned && !b.assigned) return -1;
                                if (!a.assigned && b.assigned) return 1;
                                // If both have same assigned status, sort by name
                                return a.name.localeCompare(b.name);
                              })
                              .map((permission, index) => {
                                return (
                              <div key={permission.id || index} style={{ 
                                padding: '8px', 
                                borderBottom: index < rolePermissions.length - 1 ? '1px solid #eee' : 'none',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <div>
                                  <div style={{ fontWeight: 'bold' }}>
                                    {permission.name}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {permission.description}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {/* Show toggle button only for non-system roles */}
                                  {editingItem.orgID !== 0 ? (
                                    <button
                                      onClick={() => handlePermissionToggle(permission)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '10px',
                                        color: permission.assigned ? '#d32f2f' : '#4CAF50',
                                        fontSize: '14px'
                                      }}
                                      title={permission.assigned ? 'Berechtigung entfernen' : 'Berechtigung hinzufügen'}
                                    >
                                      <FontAwesomeIcon 
                                        icon={permission.assigned ? faMinus : faPlus} 
                                        style={{ fontSize: '12px' }}
                                      />
                                    </button>
                                  ) : (
                                    /* Show read-only status for system roles */
                                    <div style={{
                                      padding: '4px 8px',
                                      borderRadius: '10px',
                                      fontSize: '12px',
                                      fontWeight: 'bold',
                                      backgroundColor: permission.assigned ? '#d4edda' : '#f8d7da',
                                      color: permission.assigned ? '#155724' : '#721c24',
                                      border: permission.assigned ? '1px solid #c3e6cb' : '1px solid #f5c6cb'
                                    }}>
                                      {permission.assigned ? '✓ Aktiv' : '✗ Inaktiv'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )})
                          )}
                        </div>
                      )}
                      {/* System role info */}
                      {editingItem.orgID === 0 && (
                        <div style={{
                          background: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          borderRadius: '10px',
                          padding: '8px',
                          marginTop: '8px',
                          color: '#6c757d',
                          fontSize: '12px'
                        }}>
                          <strong>System-Rolle:</strong> Berechtigungen können nicht geändert werden.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {dialogType === 'channel' && (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    defaultValue={editingItem?.name || ''}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333' }}>
                    Frequenz
                  </label>
                  <input
                    type="text"
                    defaultValue={editingItem?.frequency || ''}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            )}

            {dialogType === 'supervisor' && (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    defaultValue={editingItem?.name || ''}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#333' }}>
                    Maximale Anzahl Trupps
                  </label>
                  <input
                    type="number"
                    defaultValue={editingItem?.maxTeams || 1}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={handleCloseDialog}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#666',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  minWidth: '100px'
                }}
              >
                Abbrechen
              </button>
              {/* Only show save button for non-system roles or when creating new role */}
              {(!editingItem || editingItem.orgID !== 0) && (
                <button
                  onClick={handleSave}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#c00',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    minWidth: '100px'
                  }}
                >
                  Speichern
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '14px',
            padding: '30px',
            minWidth: '400px',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: '#ffebee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <FontAwesomeIcon 
                icon={faTrash} 
                style={{ 
                  fontSize: '24px', 
                  color: '#d32f2f' 
                }} 
              />
            </div>
            
            <h2 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>
              Löschen bestätigen
            </h2>
            
            <p style={{ marginBottom: '25px', color: '#666', fontSize: '16px' }}>
              Möchten Sie <strong>{deleteModal.name}</strong> wirklich löschen?
            </p>
            
            <p style={{ marginBottom: '25px', color: '#999', fontSize: '14px' }}>
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={handleCancelDelete}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#666',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#d32f2f',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
      {/* CSV Import Modal */}
      {csvImportOpen && (
        <div className="portal-modal-backdrop">
          <div className="portal-modal portal-modal--lg">
            <button
              type="button"
              className="portal-modal-close"
              aria-label="Schliessen"
              onClick={() => { setCsvImportOpen(false); setCsvPreview([]); setCsvResults(null); }}
            >
              ×
            </button>
            <h2 className="portal-modal-title">
              Benutzer via CSV importieren
            </h2>

            {/* Sample download */}
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '20px',
              border: '1px solid #e0e0e0'
            }}>
              <p style={{ margin: '0 0 10px 0', color: '#555', fontSize: '14px' }}>
                Laden Sie die Vorlage herunter, füllen Sie diese aus und importieren Sie sie anschliessend.
                Das Trennzeichen ist ein <strong>Semikolon (;)</strong>.
              </p>
              <button
                onClick={generateSampleCsv}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#1976D2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FontAwesomeIcon icon={faDownload} />
                Vorlage herunterladen
              </button>
            </div>

            {/* File upload */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 'bold' }}>
                CSV-Datei auswählen
              </label>
              <input
                ref={csvFileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvFileSelect}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '10px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Preview table */}
            {csvPreview.length > 0 && !csvResults && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                  Vorschau ({csvPreview.length} Benutzer)
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left', backgroundColor: '#f9f9f9' }}>Zeile</th>
                        <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left', backgroundColor: '#f9f9f9' }}>Vorname</th>
                        <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left', backgroundColor: '#f9f9f9' }}>Nachname</th>
                        <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left', backgroundColor: '#f9f9f9' }}>E-Mail</th>
                        <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left', backgroundColor: '#f9f9f9' }}>Telefon</th>
                        <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left', backgroundColor: '#f9f9f9' }}>RangID</th>
                        <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left', backgroundColor: '#f9f9f9' }}>Kontotyp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((row, idx) => (
                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row._row}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row['Vorname']}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row['Nachname']}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row['E-Mail']}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row['Telefon'] || '-'}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row['RangID']}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontWeight: 'bold',
                              backgroundColor: row['Kontotyp'] === '2' ? '#e3f2fd' : '#e8f5e9',
                              color: row['Kontotyp'] === '2' ? '#1565c0' : '#2e7d32'
                            }}>
                              {row['Kontotyp'] === '2' ? 'Technisch' : 'Normal'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Import results */}
            {csvResults && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Import-Ergebnis</h4>
                {csvResults.success > 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '10px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: '#2e7d32'
                  }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <strong>{csvResults.success}</strong> Benutzer erfolgreich importiert
                  </div>
                )}
                {csvResults.errors.length > 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#ffebee',
                    borderRadius: '10px',
                    color: '#c62828'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <FontAwesomeIcon icon={faTimesCircle} />
                      <strong>{csvResults.errors.length}</strong> Fehler beim Import
                    </div>
                    {csvResults.errors.map((err, idx) => (
                      <div key={idx} style={{ fontSize: '13px', padding: '4px 0', borderTop: idx > 0 ? '1px solid #ffcdd2' : 'none' }}>
                        Zeile {err.row} ({err.name}): {err.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            {csvPreview.length > 0 && !csvResults && (
              <div className="portal-modal-actions">
                <button
                  type="button"
                  onClick={handleCsvImport}
                  disabled={csvImporting}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: csvImporting ? '#999' : '#2e7d32',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: csvImporting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FontAwesomeIcon icon={faFileUpload} />
                  {csvImporting ? 'Importiere...' : `${csvPreview.length} Benutzer importieren`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel; 